import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import PaymentHistory from './PaymentHistory';
import PaymentInformation from '../PaymentInformation';
import { StoragePlanCardProps } from '../../../pages/auth/storage-plans/storage-plan-card/StoragePlanCard';
import AddPaymentMethodModal, { initialPaymentState } from '../../payment-method-modal/AddPaymentMethodModal';
import Loader from '../../loader/Loader';
import { EditSVG } from '../../svgs';
import { showToast } from '../../../services/toast.service';
import { getPaymentDetailsByAccountId } from '../../../api/paymentGatewayApi';
import { getStoragePlans } from '../../../api/storagePlansApi';
import { AddressType, PaymentActions, PaymentMethods } from '../../../types/enums';
import { PaymentMethod } from '../../../types/interfaces/paymentMethod.interface';
import { toastMessages } from '../../../constants/errorMessages';
import {
  ADDRESS_CONSTANTS,
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  PAYMENT_CONSTANTS,
  STORAGE_PLAN_CONSTANTS,
} from '../../../constants/common';
import './manageBillingInformation.scss';

type ManageBillingInformationProps = {
  locationState: { activeTab: number; addPaymentMethodModalVisible: boolean; instantPayment?: boolean };
};

type AccountPaymentMethod = {
  maskedCardNumber?: string;
  paymentMethod?: string;
  profileId?: string | null;
  name?: string;
  address?: string;
  expiry?: string | null;
  city?: string;
  region?: string;
  country?: string;
  postal?: string;
  accountType?: string;
};

const ManageBillingInformation: FC<ManageBillingInformationProps> = ({ locationState }) => {
  const navigate = useNavigate();

  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const activeAccount = useAppSelector(state => state.activeAccount);
  const paymentStatus = useAppSelector(state => state.payment.addedPaymentMethod);

  const [activeTab, setActiveTab] = useState(0);
  const [addPaymentMethodModalVisible, setAddPaymentMethodModalVisible] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<PaymentMethod>(initialPaymentState);
  const [paymentMethodsOnFile, setPaymentMethodsOnFile] = useState<AccountPaymentMethod[]>([]);
  const [storageRates, setStorageRates] = useState([
    { storagePlanId: 1, name: '', costInCents: 0, unitOfMeasurement: '', period: '' },
  ]);
  const [storageRatesLoading, setStorageRatesLoading] = useState<boolean>(false);
  const [updatedPaymentInfo, setUpdatedPaymentInfo] = useState<boolean>(false);
  const [isEditPaymentMethod, setIsEditPaymentMethod] = useState<boolean>(false);
  const [editingPaymentMethodIndex, setEditingPaymentMethodIndex] = useState<number | null>(null);

  const handleRates = async () => {
    let rates: StoragePlanCardProps[] = [];

    try {
      setStorageRatesLoading(true);
      rates = (await getStoragePlans()).data;
      if (rates) {
        setStorageRates(rates);
        setStorageRatesLoading(false);
      }
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const getPaymentDetails = async () => {
    if (accountId && paymentStatus) {
      try {
        const { data: paymentDetailsResponse } = await getPaymentDetailsByAccountId(accountId);
        const paymentMethods = Array.isArray(paymentDetailsResponse)
          ? paymentDetailsResponse
          : paymentDetailsResponse
          ? [paymentDetailsResponse]
          : [];
        setPaymentMethodsOnFile(paymentMethods);

        const cardPayment = paymentMethods.find(method => method?.paymentMethod === PaymentMethods.CARD);
        if (!cardPayment) {
          setPaymentInfo(initialPaymentState);
          return;
        }

        const accountData = {
          cardNumber: {
            value: cardPayment.maskedCardNumber,
            valid: cardPayment.maskedCardNumber?.length > 0 ? true : false,
          },
          expiry: {
            value: cardPayment.expiry ?? '',
            valid: (cardPayment.expiry ?? '').length > 0 ? true : false,
          },
          accountNumber: { value: '', valid: false },
          accountType: { value: '', name: '' },
        };

        setPaymentInfo({
          paymentType: {
            value: cardPayment?.paymentMethod,
            valid: cardPayment?.paymentMethod ? true : false,
          },
          name: { value: cardPayment?.name, valid: cardPayment?.name?.length > 0 ? true : false },
          address: { value: cardPayment?.address, valid: cardPayment?.address?.length > 0 ? true : false },
          city: { value: cardPayment?.city, valid: cardPayment?.city?.length > 0 ? true : false },
          region: { value: cardPayment?.region, valid: cardPayment?.region?.length > 0 ? true : false },
          postalCode: { value: cardPayment?.postal, valid: cardPayment?.postal?.length > 0 ? true : false },
          country: { value: cardPayment?.country, valid: cardPayment?.country?.length > 0 ? true : false },
          routingNumber: { value: '', valid: false },
          cvv: { value: '', valid: false },
          ...accountData,
        });
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setUpdatedPaymentInfo(false);
      }
    }
  };

  useEffect(() => {
    if (locationState != null) {
      setActiveTab(locationState?.activeTab);
      setAddPaymentMethodModalVisible(locationState?.addPaymentMethodModalVisible);
      navigate(location.pathname, { replace: true });
    }
  }, [locationState, navigate]);

  useEffect(() => {
    handleRates();
  }, []);

  useEffect(() => {
    getPaymentDetails();
  }, [updatedPaymentInfo]);

  const cardNumberFormat = (method: AccountPaymentMethod) => {
    const maskedNumber = method?.maskedCardNumber ?? '';
    if (!maskedNumber) return '';
    if (method?.paymentMethod === PaymentMethods.CARD) {
      return maskedNumber
        .replace(/\s/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim();
    }

    let pattern = '';
    for (let index = 0; index < maskedNumber.length - 4; index++) {
      pattern += 'x';
    }
    return `${pattern}${maskedNumber.slice(-4)}`
      .replace(/\s/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim();
  };

  const mapApiPaymentMethodToForm = (billing: AccountPaymentMethod): PaymentMethod => {
    const isCardPayment = billing?.paymentMethod === PaymentMethods.CARD;
    const achMaskedNumber = !isCardPayment ? billing?.maskedCardNumber ?? '' : '';
    const usesExistingMaskedAccount = !isCardPayment && /X/i.test(achMaskedNumber);
    return {
      paymentType: {
        value: billing?.paymentMethod ?? PaymentMethods.CARD,
        valid: billing?.paymentMethod ? true : false,
      },
      name: { value: billing?.name ?? '', valid: (billing?.name ?? '').length > 0 },
      address: { value: billing?.address ?? '', valid: (billing?.address ?? '').length > 0 },
      city: { value: billing?.city ?? '', valid: (billing?.city ?? '').length > 0 },
      region: { value: billing?.region ?? '', valid: (billing?.region ?? '').length > 0 },
      postalCode: { value: billing?.postal ?? '', valid: (billing?.postal ?? '').length > 0 },
      country: { value: billing?.country ?? '', valid: (billing?.country ?? '').length > 0 },
      routingNumber: { value: '', valid: false },
      cvv: { value: '', valid: false },
      cardNumber: {
        value: isCardPayment ? billing?.maskedCardNumber ?? '' : '',
        valid: isCardPayment ? (billing?.maskedCardNumber ?? '').length > 0 : false,
      },
      expiry: {
        value: isCardPayment ? billing?.expiry ?? '' : '',
        valid: isCardPayment ? (billing?.expiry ?? '').length > 0 : false,
      },
      accountNumber: {
        value: !isCardPayment ? billing?.maskedCardNumber ?? '' : '',
        valid: !isCardPayment ? (billing?.maskedCardNumber ?? '').length > 0 : false,
      },
      accountType: {
        value: !isCardPayment ? billing?.accountType ?? '' : '',
        name: !isCardPayment ? billing?.accountType ?? '' : '',
      },
      ...(isCardPayment
        ? {}
        : {
            profileId: billing?.profileId ?? null,
            usesExistingMaskedAccount,
          }),
    };
  };

  const paymentMethodsArray = Array.isArray(paymentMethodsOnFile) ? paymentMethodsOnFile : [];
  const hasCardPaymentMethod = paymentMethodsArray.some(method => method?.paymentMethod === PaymentMethods.CARD);
  const hasAchPaymentMethod = paymentMethodsArray.some(method => method?.paymentMethod === PaymentMethods.ACH);

  const showAddPaymentMethodButton = !hasCardPaymentMethod && !hasAchPaymentMethod;

  const addPaymentMethod = () => {
    const billing = activeAccount?.addresses?.find(address => address?.type === AddressType.BILLING);

    const baseForAdd: PaymentMethod = hasCardPaymentMethod
      ? {
          ...initialPaymentState,
          paymentType: { value: PaymentMethods.ACH, valid: true },
          cardNumber: { value: '', valid: false },
          expiry: { value: '', valid: false },
          cvv: { value: '', valid: false },
          routingNumber: { value: '', valid: false },
          accountNumber: { value: '', valid: false },
          accountType: { value: '', name: '' },
          usesExistingMaskedAccount: false,
        }
      : { ...initialPaymentState };

    setPaymentInfo(
      billing
        ? {
            ...baseForAdd,
            address: { value: billing?.streetAddress, valid: Boolean(billing.streetAddress?.length) },
            city: { value: billing?.city, valid: Boolean(billing.city?.length) },
            region: { value: billing.state, valid: Boolean(billing.state?.length) },
            postalCode: { value: billing.postalCode, valid: Boolean(billing.postalCode?.length) },
            country: { value: billing.country, valid: Boolean(billing.country?.length) },
          }
        : baseForAdd,
    );

    setIsEditPaymentMethod(false);
    setEditingPaymentMethodIndex(null);
    setAddPaymentMethodModalVisible(true);
  };

  const onEditPaymentMethod = (method: AccountPaymentMethod, methodIndex: number) => {
    setPaymentInfo(mapApiPaymentMethodToForm(method));
    setIsEditPaymentMethod(true);
    setEditingPaymentMethodIndex(methodIndex);
    setAddPaymentMethodModalVisible(true);
  };

  const addPaymentMethodButtonLabel =
    paymentMethodsOnFile.length > 0 && !hasCardPaymentMethod ? PAYMENT_CONSTANTS.ADD_CARD : BUTTON_CONSTANTS.ADD_PAYMENT_METHOD;

  return (
    <>
      <div className="manage-billing-information">
        <h4 className="top-header">{LABEL_CONSTANTS.MANAGE_YOUR_BILLING}</h4>
        <div className="manage-billing-info__header">
          <ul className="manage-billing-info__tabs">
            <li className={activeTab === 0 ? 'active' : ''} data-testid="active-tab-0" onClick={() => setActiveTab(0)}>
              <div className="storage-rates">{LABEL_CONSTANTS.STORAGE_RATES}</div>
            </li>
            <li className={activeTab === 1 ? 'active' : ''} data-testid="active-tab-1" onClick={() => setActiveTab(1)}>
              <div className="payment-history">{LABEL_CONSTANTS.PAYMENT_HISTORY}</div>
            </li>
            <li className={activeTab === 2 ? 'active' : ''} data-testid="active-tab-2" onClick={() => setActiveTab(2)}>
              <div className="payment-method">{LABEL_CONSTANTS.PAYMENT_METHOD}</div>
            </li>
          </ul>
        </div>

        <div className="manage-billing-info__body">
          {activeTab === 0 && (
            <>
              {storageRatesLoading ? (
                <div className="storage-rates-loader">
                  <Loader addedSpace loaderSize="small" />
                </div>
              ) : (
                <PaymentInformation
                  header={''}
                  description={STORAGE_PLAN_CONSTANTS.STORAGE_RATES_SUBHEADER}
                  columnsData={storageRates?.map(storage => {
                    return {
                      storagePlanId: storage?.storagePlanId,
                      quantityHeader: storage?.name,
                      costHeader: storage?.costInCents,
                      unitOfMeasurement: storage?.unitOfMeasurement,
                      period: storage?.period,
                    };
                  })}
                />
              )}
            </>
          )}

          {activeTab === 1 && (
            <div className="payment-history">
              <PaymentHistory />
            </div>
          )}

          {activeTab === 2 && (
            <>
              {paymentStatus != undefined && (
                <>
                  {paymentMethodsArray.length > 0 ? (
                    <div className="payment-method">
                      {paymentMethodsArray.map((method, index) => (
                        <div className="card" key={`${method?.paymentMethod}-${method?.maskedCardNumber}-${index}`}>
                          <div className="payment-card-header">
                            <h4>
                              {method?.paymentMethod === PaymentMethods.CARD ? PAYMENT_CONSTANTS.CARD : method?.paymentMethod}
                            </h4>
                            <button
                              className="icon button transparent edit-button"
                              data-testid="edit-button"
                              onClick={() => onEditPaymentMethod(method, index)}>
                              <EditSVG />
                            </button>
                          </div>
                          <div className="payment-card-body">
                            <div className="card-number">
                              <span>{cardNumberFormat(method)}</span>
                            </div>
                            <div className="billing-address">
                              <h5>{ADDRESS_CONSTANTS.ADDRESS}:</h5>
                              <p>
                                {method?.address}, {method?.city}, {method?.region} {method?.postal} {method?.country}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {showAddPaymentMethodButton && (
                        <div className="add-payment-method-button">
                          <button className="button outlined" onClick={() => addPaymentMethod()}>
                            {addPaymentMethodButtonLabel}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="add-payment-method-button">
                      <button className="button outlined" onClick={() => addPaymentMethod()}>
                        {BUTTON_CONSTANTS.ADD_PAYMENT_METHOD}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {accountId && addPaymentMethodModalVisible && (
        <AddPaymentMethodModal
          title={isEditPaymentMethod ? LABEL_CONSTANTS.UPDATE_PAYMENT_METHOD : LABEL_CONSTANTS.ADD_PAYMENT_METHOD}
          accountId={accountId}
          paymentDetails={paymentInfo}
          isOpen={addPaymentMethodModalVisible}
          setIsOpen={setAddPaymentMethodModalVisible}
          onclick={() => {
            setAddPaymentMethodModalVisible(false);
            setEditingPaymentMethodIndex(null);
          }}
          buttonAction={isEditPaymentMethod ? PaymentActions.UPDATE_PAYMENT_METHOD : PaymentActions.ADD_PAYMENT_METHOD}
          accountHasCard={hasCardPaymentMethod}
          hideDefaultPaymentOption={isEditPaymentMethod}
          setUpdatedPaymentInfo={setUpdatedPaymentInfo}
        />
      )}
    </>
  );
};

export default ManageBillingInformation;
